import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import api from "../lib/api";
import { API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import {
  Buildings, Envelope, Phone, Paperclip, X, MagnifyingGlass,
  FileText, Image as ImageIcon, FilePdf, DownloadSimple, Trash,
  ChatTeardropText, PencilSimple
} from "@phosphor-icons/react";

const STATUS_OPTIONS = [
  { value: "new", label: "New", color: "bg-blue-50 text-blue-700 border-blue-200" },
  { value: "contacted", label: "Contacted", color: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  { value: "qualified", label: "Qualified", color: "bg-purple-50 text-purple-700 border-purple-200" },
  { value: "converted", label: "Converted", color: "bg-green-50 text-green-700 border-green-200" },
  { value: "closed", label: "Closed", color: "bg-slate-100 text-slate-600 border-slate-200" },
];
const STATUS_STYLE = Object.fromEntries(STATUS_OPTIONS.map(s => [s.value, s.color]));

const fmtDate = (iso) => iso ? new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—";
const fmtBytes = (n) => n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(2)} MB` : `${(n / 1024).toFixed(1)} KB`;

const AttachmentIcon = ({ type }) => {
  if (!type) return <Paperclip size={18} />;
  if (type.startsWith("image/")) return <ImageIcon size={18} weight="fill" className="text-blue-500" />;
  if (type === "application/pdf") return <FilePdf size={18} weight="fill" className="text-red-500" />;
  return <FileText size={18} weight="fill" className="text-slate-500" />;
};

const AdminEnquiries = ({ user, logout }) => {
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selected, setSelected] = useState(null); // full enquiry with attachments

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [eRes, uRes] = await Promise.all([
        api.get(`${API}/admin/product-enquiries`),
        api.get(`/users`),
      ]);
      setItems(eRes.data);
      setUsers(uRes.data.filter(u => ["bde", "bdm", "manager"].includes(u.role)));
    } catch { toast.error("Failed to load enquiries"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openDetail = async (enq) => {
    try {
      const { data } = await api.get(`${API}/admin/product-enquiries/${enq.enquiry_id}`);
      setSelected(data);
    } catch { toast.error("Failed to load enquiry"); }
  };

  const updateField = async (enquiryId, patch) => {
    try {
      await api.put(`${API}/admin/product-enquiries/${enquiryId}?requester_role=${user?.role || ""}`, patch);
      toast.success("Updated");
      load();
      if (selected?.enquiry_id === enquiryId) {
        setSelected(s => ({ ...s, ...patch, updated_at: new Date().toISOString() }));
      }
    } catch { toast.error("Update failed"); }
  };

  const deleteEnquiry = async (enquiryId) => {
    if (!window.confirm("Delete this enquiry? This will also remove the linked lead.")) return;
    try {
      await api.delete(`${API}/admin/product-enquiries/${enquiryId}?requester_role=${user?.role || ""}`);
      toast.success("Deleted");
      setSelected(null);
      load();
    } catch { toast.error("Delete failed"); }
  };

  const filtered = useMemo(() => {
    let list = items;
    if (statusFilter) list = list.filter(i => (i.status || "new") === statusFilter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(i =>
        (i.name || "").toLowerCase().includes(q) ||
        (i.email || "").toLowerCase().includes(q) ||
        (i.company || "").toLowerCase().includes(q) ||
        (i.product_name || "").toLowerCase().includes(q)
      );
    }
    return list;
  }, [items, statusFilter, search]);

  const counts = useMemo(() => {
    const c = { total: items.length };
    STATUS_OPTIONS.forEach(s => { c[s.value] = items.filter(i => (i.status || "new") === s.value).length; });
    return c;
  }, [items]);

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar user={user} logout={logout} />
      <div className="flex-1 ml-64">
        <div className="sticky top-0 bg-white border-b border-slate-200 z-30 shadow-sm">
          <div className="px-8 py-5 flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2" style={{ fontFamily: "Outfit, sans-serif" }}>
                <ChatTeardropText size={24} weight="fill" className="text-[#002FA7]" /> Product Enquiries
              </h1>
              <p className="text-xs text-slate-500 uppercase tracking-[0.2em] mt-1">{counts.total} total · auto-assigned round-robin</p>
            </div>
          </div>
        </div>

        <div className="p-8 space-y-5">
          {/* Status KPI tiles (also filter) */}
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
            <button onClick={() => setStatusFilter("")}
              data-testid="enq-filter-all"
              className={`p-3 text-left border transition ${statusFilter === "" ? 'bg-[#002FA7] text-white border-[#002FA7]' : 'bg-white border-slate-200 hover:border-[#002FA7]'}`}>
              <div className="text-xs uppercase tracking-wider font-bold opacity-80">All</div>
              <div className="text-2xl font-bold" style={{ fontFamily: "Outfit, sans-serif" }}>{counts.total}</div>
            </button>
            {STATUS_OPTIONS.map(s => (
              <button key={s.value}
                onClick={() => setStatusFilter(statusFilter === s.value ? "" : s.value)}
                data-testid={`enq-filter-${s.value}`}
                className={`p-3 text-left border transition ${statusFilter === s.value ? s.color + ' border-current ring-2 ring-offset-1 ring-current' : 'bg-white border-slate-200 hover:border-slate-400'}`}>
                <div className="text-xs uppercase tracking-wider font-bold">{s.label}</div>
                <div className="text-2xl font-bold" style={{ fontFamily: "Outfit, sans-serif" }}>{counts[s.value] || 0}</div>
              </button>
            ))}
          </div>

          {/* Search */}
          <div className="relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input data-testid="enq-search" placeholder="Search by name, email, company, product…"
              value={search} onChange={e => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-slate-300 focus:border-[#002FA7] outline-none" />
          </div>

          {/* Table */}
          <div className="bg-white border border-slate-200 shadow-sm overflow-hidden">
            {loading ? (
              <div className="flex justify-center py-20"><div className="loading-spinner" /></div>
            ) : filtered.length === 0 ? (
              <div className="p-16 text-center text-slate-400">
                <ChatTeardropText size={40} weight="duotone" className="mx-auto text-slate-300 mb-2" />
                No enquiries {statusFilter || search ? "match the current filter" : "yet"}
              </div>
            ) : (
              <table className="w-full text-sm" data-testid="enq-table">
                <thead className="bg-slate-50 text-left">
                  <tr>
                    <th className="px-4 py-3 text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Customer</th>
                    <th className="px-4 py-3 text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Product</th>
                    <th className="px-4 py-3 text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Assigned</th>
                    <th className="px-4 py-3 text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Status</th>
                    <th className="px-4 py-3 text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Files</th>
                    <th className="px-4 py-3 text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Received</th>
                    <th className="px-4 py-3 text-right text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Actions</th>
                  </tr>
                </thead>
                <tbody data-testid="enq-rows">
                  {filtered.map(it => (
                    <tr key={it.enquiry_id} data-testid={`enq-row-${it.enquiry_id}`}
                      className="border-t border-slate-100 hover:bg-slate-50/60 cursor-pointer transition"
                      onClick={() => openDetail(it)}>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-slate-800">{it.name}</div>
                        <div className="text-xs text-slate-500 flex items-center gap-2 mt-0.5">
                          <Envelope size={11} /> {it.email}
                          {it.phone && <><Phone size={11} className="ml-2" /> {it.phone}</>}
                        </div>
                        {it.company && <div className="text-xs text-slate-400 mt-0.5 flex items-center gap-1"><Buildings size={11} />{it.company}</div>}
                      </td>
                      <td className="px-4 py-3 text-slate-700">{it.product_name || "—"}</td>
                      <td className="px-4 py-3">
                        <select value={it.assigned_to_id || ""}
                          onClick={e => e.stopPropagation()}
                          onChange={e => {
                            e.stopPropagation();
                            const u = users.find(x => x.user_id === e.target.value);
                            updateField(it.enquiry_id, { assigned_to_id: e.target.value, assigned_to: u ? u.name : "" });
                          }}
                          data-testid={`enq-assign-${it.enquiry_id}`}
                          className="border border-slate-200 text-xs px-2 py-1 bg-white">
                          <option value="">Unassigned</option>
                          {users.map(u => <option key={u.user_id} value={u.user_id}>{u.name} ({u.role})</option>)}
                        </select>
                      </td>
                      <td className="px-4 py-3">
                        <select value={it.status || "new"}
                          onClick={e => e.stopPropagation()}
                          onChange={e => { e.stopPropagation(); updateField(it.enquiry_id, { status: e.target.value }); }}
                          data-testid={`enq-status-${it.enquiry_id}`}
                          className={`text-xs px-2 py-1 border font-semibold ${STATUS_STYLE[it.status || "new"]}`}>
                          {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                        </select>
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-600">
                        {(it.attachments || []).length > 0 ? (
                          <span className="inline-flex items-center gap-1"><Paperclip size={12} /> {it.attachments.length}</span>
                        ) : "—"}
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-500">{fmtDate(it.created_at)}</td>
                      <td className="px-4 py-3 text-right">
                        <button onClick={e => { e.stopPropagation(); openDetail(it); }}
                          data-testid={`enq-view-${it.enquiry_id}`}
                          className="p-1.5 text-[#002FA7] hover:bg-blue-50" title="View"><PencilSimple size={16} /></button>
                        <button onClick={e => { e.stopPropagation(); deleteEnquiry(it.enquiry_id); }}
                          data-testid={`enq-delete-${it.enquiry_id}`}
                          className="p-1.5 text-red-500 hover:bg-red-50 ml-1" title="Delete"><Trash size={16} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {selected && (
        <EnquiryDetail
          enq={selected} users={users}
          onClose={() => setSelected(null)}
          onUpdate={(patch) => updateField(selected.enquiry_id, patch)}
        />
      )}
    </div>
  );
};

const EnquiryDetail = ({ enq, users, onClose, onUpdate }) => {
  const [notes, setNotes] = useState(enq.internal_notes || "");

  const saveNotes = () => {
    if (notes === (enq.internal_notes || "")) return;
    onUpdate({ internal_notes: notes });
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="enq-detail-modal">
      <div className="bg-white shadow-2xl w-full max-w-3xl max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between z-10">
          <div>
            <h3 className="text-xl font-bold" style={{ fontFamily: "Outfit, sans-serif" }}>{enq.name}</h3>
            <p className="text-xs text-slate-500 mt-0.5">Enquiry · {fmtDate(enq.created_at)}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100" data-testid="enq-detail-close"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <Info icon={<Envelope size={14} />} label="Email" value={enq.email} />
            <Info icon={<Phone size={14} />} label="Phone" value={enq.phone} />
            <Info icon={<Buildings size={14} />} label="Company" value={enq.company || "—"} />
            <Info label="Product" value={enq.product_name || "—"} />
            <Info label="Budget" value={enq.budget || "—"} />
            <Info label="Timeline" value={enq.timeline || "—"} />
          </div>

          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 mb-2">Requirement</div>
            <div className="bg-slate-50 border border-slate-200 p-4 text-sm whitespace-pre-wrap" data-testid="enq-detail-requirement">{enq.requirement || "—"}</div>
          </div>

          {enq.attachments?.length > 0 && (
            <div>
              <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 mb-2">Attachments ({enq.attachments.length})</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="enq-attachments-preview">
                {enq.attachments.map((a, i) => (
                  <AttachmentCard key={`${a.name}-${i}`} attachment={a} />
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 mb-2">Status</div>
              <select value={enq.status || "new"}
                onChange={e => onUpdate({ status: e.target.value })}
                data-testid="enq-detail-status"
                className="w-full border border-slate-300 px-3 py-2 text-sm">
                {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 mb-2">Assigned To</div>
              <select value={enq.assigned_to_id || ""}
                onChange={e => {
                  const u = users.find(x => x.user_id === e.target.value);
                  onUpdate({ assigned_to_id: e.target.value, assigned_to: u ? u.name : "" });
                }}
                data-testid="enq-detail-assign"
                className="w-full border border-slate-300 px-3 py-2 text-sm">
                <option value="">Unassigned</option>
                {users.map(u => <option key={u.user_id} value={u.user_id}>{u.name} ({u.role})</option>)}
              </select>
            </div>
          </div>

          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 mb-2">Internal Notes</div>
            <textarea value={notes} onChange={e => setNotes(e.target.value)}
              onBlur={saveNotes}
              placeholder="Follow-up notes, quotation sent, objections, next steps…"
              data-testid="enq-detail-notes"
              rows={4}
              className="w-full border border-slate-300 focus:border-[#002FA7] px-3 py-2 text-sm outline-none" />
            <p className="text-xs text-slate-400 mt-1">Auto-saves on blur</p>
          </div>
        </div>
      </div>
    </div>
  );
};

const Info = ({ icon, label, value }) => (
  <div>
    <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 flex items-center gap-1">{icon}{label}</div>
    <div className="text-sm text-slate-800 mt-1 break-words">{value || "—"}</div>
  </div>
);

const AttachmentCard = ({ attachment }) => {
  const [showImg, setShowImg] = useState(false);
  const isImage = attachment.content_type?.startsWith("image/");
  return (
    <div className="border border-slate-200 bg-slate-50 p-3">
      <div className="flex items-center gap-2 mb-2">
        <AttachmentIcon type={attachment.content_type} />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium truncate">{attachment.name}</div>
          <div className="text-xs text-slate-500">{fmtBytes(attachment.size || 0)} · {attachment.content_type}</div>
        </div>
      </div>
      {isImage && attachment.data_url && (
        <div>
          <button onClick={() => setShowImg(x => !x)} className="text-xs text-[#002FA7] font-bold hover:underline">
            {showImg ? "Hide preview" : "Show preview"}
          </button>
          {showImg && <img src={attachment.data_url} alt={attachment.name} className="mt-2 max-h-64 w-auto border border-slate-200" />}
        </div>
      )}
      {attachment.data_url && (
        <a href={attachment.data_url} download={attachment.name}
          className="mt-2 inline-flex items-center gap-1 text-xs text-[#002FA7] font-bold hover:underline"
          data-testid={`enq-download-${attachment.name}`}>
          <DownloadSimple size={12} /> Download
        </a>
      )}
    </div>
  );
};

export default AdminEnquiries;
